import React, {useCallback, useState, useEffect, useMemo} from 'react';
import {useOutletContext} from "react-router";
import {Box, Button, Checkbox, Typography} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import {PlayerInfoDialog} from "../dialogs/PlayerInfoDialog";
import {PlayerStatsDialog} from "../dialogs/PlayerStatsDialog";
import {DropPlayerDialog} from "../dialogs/DropPlayerDialog";
import {config} from "../../config";
import {apiGet, apiPost, formatTimestampWithDOW, getUserName, rosterGridTypes} from "../../utils";

export const RosterGrid = ({type, managerName, seasonYear, weekNum, showPoints, showSchedule, onDropButtonClick, onTradeCheckboxClick, rosterOverride, spentOverride, loadingOverride}) => {
    const {currentLeague, currentAuction} = useOutletContext();
    const [roster, setRoster] = useState([]);
    const [spent, setSpent] = useState(0);
    const [spentSalaries, setSpentSalaries] = useState(0);
    const [spentPenalties, setSpentPenalties] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openPlayerInfo, setOpenPlayerInfo] = useState(false);
    const [dropDialogOpen, setDropDialogOpen] = useState(false);
    const [openPlayerStats, setOpenPlayerStats] = useState(false);
    const [playerInfoRow, setPlayerInfoRow] = useState({});
    const [playerStatsRow, setPlayerStatsRow] = useState({});

    const sortRosterRows = useCallback((rows) => {
        rows.sort((a, b) => {
            if (a.is_penalty && !b.is_penalty) return 1;
            if (!a.is_penalty && b.is_penalty) return -1;
            if (!a.is_penalty && !b.is_penalty) {
                if (a.display_order > b.display_order) return 1;
                if (a.display_order < b.display_order) return -1;
                if (!a.salary || a.salary < b.salary) return 1;
                if (!b.salary || a.salary > b.salary) return -1;
            } else {
                if (a.signed_year > b.signed_year) return 1;
                if (a.signed_year < b.signed_year) return -1;
                if (a.penalty < b.penalty) return 1;
                if (a.penalty > b.penalty) return -1;
            }
            if (a.last_name > b.last_name) return 1;
            if (a.last_name < b.last_name) return -1;
            if (a.first_name > b.first_name) return 1;
            if (a.first_name < b.first_name) return -1;
            return 0;
        });
        renumberRosterRows(rows);
    }, []);

    const renumberRosterRows = (rows) => {
        let owned = 0;
        for (let i = 0; i < rows.length; i++) {
            if (!rows[i].is_penalty) {
                rows[i].row_number = i + 1;
                owned++;
            } else {
                rows[i].row_number = i + 1 - owned;
            }
        }
    };

    const fetchRosterData = useCallback((year, week) => {
        setLoading(true);
        if (rosterOverride) {
            const rosterOverrideCopy = JSON.parse(JSON.stringify(rosterOverride));
            sortRosterRows(rosterOverrideCopy);
            setRoster(rosterOverrideCopy);
            setSpent(spentOverride);
            setLoading(false);
        } else {
            if (year && week !== null) {
                if (!(year === currentAuction.season_year && week === currentAuction.week_num)) {
                    apiGet(`roster/manager/${managerName}/season/${year}/week/${week}`, (data) => {
                        if (data.data) {
                            setRoster(data.data);
                            setSpent(data.data.map(c => c.salary ? c.salary : c.penalty).reduce((a, b) => a + b));
                            setSpentSalaries(data.data.map(c => c.salary ? c.salary : 0).reduce((a, b) => a + b));
                            setSpentPenalties(data.data.map(c => c.penalty).reduce((a, b) => a + b));
                            setLoading(false);
                        }
                    });
                } else {
                    const pullProjectedData = type === rosterGridTypes.PROJECTED || (year === currentAuction.season_year && currentLeague.season_phase === 'PREDRAFT');
                    const fetchesCallback = (ownedData, penaltyData, rfaData) => {
                        const combinedRoster = [...ownedData, ...(rfaData ? rfaData : []), ...(penaltyData ? penaltyData : [])];
                        sortRosterRows(combinedRoster);
                        setRoster(combinedRoster);
                        setSpent((ownedData ? ownedData.map(contract => contract.salary).reduce((a, b) => a + b) : 0) +
                            (penaltyData.length > 0 ? penaltyData.map(contract => contract.penalty).reduce((a, b) => a + b) : 0));
                        setSpentSalaries((ownedData ? ownedData.map(contract => contract.salary).reduce((a, b) => a + b) : 0));
                        setSpentPenalties((penaltyData.length > 0 ? penaltyData.map(contract => contract.penalty).reduce((a, b) => a + b) : 0));
                        setLoading(false);
                    }
                    apiGet(`contract/owned/${managerName}${pullProjectedData ? '/projected' : ''}`, (ownedData) => {
                        apiGet(`contract/penalty/${managerName}${pullProjectedData ? '/projected' : ''}`, (penaltyData) => {
                            if (['PREDRAFT', 'DRAFT'].includes(currentLeague.season_phase)) {
                                apiGet(`contract/rfa/${managerName}`, (rfaData) => {
                                    fetchesCallback(ownedData.data, penaltyData.data, rfaData.data);
                                });
                            } else {
                                fetchesCallback(ownedData.data, penaltyData.data);
                            }
                        });
                    });
                }
            }
        }
    }, [type, managerName, currentLeague, currentAuction, rosterOverride, spentOverride, sortRosterRows]);

    const columnModels = useMemo(() => {
        const renderYear = (params) => {
            return params.row.is_rfa ? 'RFA' : params.row.signed_year;
        };

        const renderSalaryAmount = (params) => {
            return params.row.salary ? params.row.salary : '';
        };

        const renderDropPenalty = (params) => {
            return params.row.penalty > 0 ? params.row.penalty : ' ';
        };

        const renderProjectedDropPenalty = (params) => {
            return params.row.penalty_next_year > 0 ? params.row.penalty_next_year : ' ';
        };

        const renderDropButton = (params) => {
            if (params.row.dropped_year) {
                return (<div/>);
            } else {
                const onClick = async () => {
                    onDropButtonClick(params.row);
                }
                return (
                    <Button color="primary" onClick={onClick} style={{padding: 0}}>Drop</Button>
                )
            }
        };

        const renderTradeCheckbox = (params) => {
            //TODO: DataGrid column def could have checkboxSelection property...?
            const handleChange = async (event) => {
                onTradeCheckboxClick(params.row, event.target.checked);
            }
            return (
                <Checkbox onChange={handleChange} style={{padding: 0}}/>
            )
        };

        const renderFantasyPoints = (params) => {
            return params.row.started ? <b>{params.row.points}</b> : params.row.points;
        };

        const common = {sortable: false, disableColumnMenu: true, headerClassName: 'grid-header'};
        const colDefs = {
            rosterColumns: [
                {...common, width: 55, headerName: 'Num', field: 'row_number', type: 'number', headerAlign: 'right'},
                {...common, width: 110, headerName: 'First Name', field: 'first_name'},
                {...common, width: 130, headerName: 'Last Name', field: 'last_name'},
                {...common, width: 50, headerName: 'Pos', field: 'pos', headerAlign: 'center', align: 'center'},
                {...common, width: 60, headerName: 'Team', field: 'team', headerAlign: 'center', align: 'center'},
                {...common, width: 60, headerName: 'Year', field: 'signed_year', headerAlign: 'center', align: 'center', renderCell: renderYear}
            ],
            normalMoneyColumns: [
                {...common, width: 75, headerName: 'Salary', field: 'salary', type: 'number', headerAlign: 'right', renderCell: renderSalaryAmount},
                {...common, width: 80, headerName: 'Penalty', field: 'penalty', type: 'number', headerAlign: 'right', renderCell: renderDropPenalty}
            ],
            projectedMoneyColumns: [
                {...common, width: 75, headerName: 'New Sal', field: 'salary', type: 'number', headerAlign: 'right', renderCell: renderSalaryAmount},
                {...common, width: 80, headerName: 'Drop Pre', field: 'penalty', type: 'number', headerAlign: 'right', renderCell: renderDropPenalty},
                {...common, width: 85, headerName: 'Drop Post', field: 'penalty_next_year', type: 'number', headerAlign: 'right', renderCell: renderProjectedDropPenalty}
            ],
            actionColumns: [
                {...common, width: 85, headerName: ' ', field: 'drop', align: 'center', renderCell: renderDropButton}
            ],
            tradeColumns: [
                {...common, width: 50, headerName: ' ', field: 'trade', align: 'center', renderCell: renderTradeCheckbox}
            ],
            pointsColumns: [
                {...common, width: 155, headerName: 'Fantasy Points', field: 'points', type: 'number', headerAlign: 'right', renderCell: renderFantasyPoints}
            ],
            scheduleColumns: [
                {...common, width: 155, headerName: 'Game Time', field: 'game_time', headerAlign: 'left', align: 'left', valueGetter: v => v ? formatTimestampWithDOW(v) : ''}
            ]
        };

        const calcWidth = (cols) => {
            return cols.reduce((t, c) => t + c.width, 0) + 2;
        };

        const salariesModel = [...colDefs.rosterColumns, ...colDefs.normalMoneyColumns];
        const pointsModel = [...colDefs.rosterColumns, ...colDefs.pointsColumns];
        const scheduleModel = [...colDefs.rosterColumns, ...colDefs.scheduleColumns];
        const projectedModel = [...colDefs.rosterColumns, ...colDefs.projectedMoneyColumns];
        const tradingModel = [...colDefs.rosterColumns, ...colDefs.normalMoneyColumns, ...colDefs.tradeColumns];
        const actionsModel = [...colDefs.rosterColumns, ...colDefs.normalMoneyColumns, ...colDefs.actionColumns];

        return {
            salaries: {cols: salariesModel, width: calcWidth(salariesModel)},
            points: {cols: pointsModel, width: calcWidth(pointsModel)},
            schedule: {cols: scheduleModel, width: calcWidth(scheduleModel)},
            projected: {cols: projectedModel, width: calcWidth(projectedModel)},
            trading: {cols: tradingModel, width: calcWidth(tradingModel)},
            actions: {cols: actionsModel, width: calcWidth(actionsModel)}
        };
    }, [onDropButtonClick, onTradeCheckboxClick]);

    const displayColumns = useMemo(() => {
        if (type === rosterGridTypes.STANDARD) {
            return showPoints ? columnModels.points : showSchedule ? columnModels.schedule : columnModels.salaries;
        } else {
            return columnModels[type];
        }
    }, [type, columnModels, showPoints, showSchedule]);

    useEffect(() => {
        fetchRosterData(seasonYear, weekNum);
    }, [fetchRosterData, seasonYear, weekNum]);

    useEffect(() => {
        setLoading(loadingOverride);
    }, [loadingOverride]);

    const determineRowClass = (params) => {
        let classes;
        if ('is_penalty' in params.row) {
            classes = `${params.row.is_penalty ? 'penalty' : params.row.display_order % 2 === 1 ? 'table-row-odd' : 'table-row-even'}`;
            classes += params.row.proposed ? ' proposed-trade' : '';
        } else {
            classes = `${!params.row.is_owned ? 'penalty' : params.row.display_order % 2 === 1 ? 'table-row-odd' : 'table-row-even'}`;
            classes += params.row.transaction_key || params.row.is_rfa ? '' : ' needs-yahoo-sync';
            classes += params.row.proposed ? ' proposed-trade' : '';
            classes += params.row.is_rfa ? ' is-rfa' : '';
        }
        return classes;
    };

    const determineOverRosterSize = (roster) => {
        const numOwned = roster ? roster.reduce((total, curr) => curr.is_penalty ? total : total + 1, 0) : 0;
        return numOwned > config.maxRosterSize;
    };

    const determineOverBudget = (spent) => {
        return spent > config.salaryCap;
    };

    const calculateFantasyPointsTotal = (roster) => {
        return roster ? roster.reduce((total, curr) => curr.started ? total + parseFloat(curr.points) : total, 0) : 0;
    };

    const handleCellClick = (params) => {
        if (params.field === 'points' && params.row.points) {
            setPlayerStatsRow(params.row);
            setOpenPlayerStats(true);
        } else if (['row_num', 'first_name', 'last_name', 'pos', 'team', 'signed_year', 'salary', 'penalty'].includes(params.field)) {
            setPlayerInfoRow(params.row);
            setOpenPlayerInfo(true);
        }
    };

    const playerIsDroppable = (isPenalty) => {
        if (!currentAuction || !seasonYear || !weekNum) return false;
        return !isPenalty && managerName === getUserName() && seasonYear === currentAuction.season_year && weekNum === currentAuction.week_num;
    };

    const handleOpenDropDialog = (row) => {
        setDropDialogOpen(true);
    };

    const handleDropPlayer = () => {
        setDropDialogOpen(false);
        const dropPostBody = {
            playerYahooId: playerInfoRow.yahoo_id,
            managerName: getUserName()
        }
        apiPost(`contract/drop${currentLeague.season_phase === 'PREDRAFT' ? '/predraft' : ''}`, dropPostBody, () => {
            setPlayerInfoRow({});
            setOpenPlayerInfo(false);
            fetchRosterData(seasonYear, weekNum);
        });
    };

    const handleCloseDropDialog = () => {
        setDropDialogOpen(false);
    };

    const handleClosePlayerInfo = () => {
        setOpenPlayerInfo(false);
        setPlayerInfoRow({});
    };

    const handleClosePlayerStats = () => {
        setOpenPlayerStats(false);
        setPlayerStatsRow({});
    };

    return (
        <div style={{width: displayColumns.width, marginLeft: 10, marginRight: 10}}>
            {loading && <LinearProgress color="secondary"/>}
            <DataGrid columns={displayColumns.cols} rows={roster} hideFooterPagination={true}
                      columnHeaderHeight={30} rowHeight={23}
                      getRowClassName={(params) => (determineRowClass(params))}
                      disableRowSelectionOnClick disableMultipleRowSelection disableColumnResize
                      onCellClick={handleCellClick}
                      slots={{footer: RosterGridFooter}} slotProps={{
                footer: {
                    spent: spent,
                    spentSalaries: spentSalaries,
                    spentPenalties: spentPenalties,
                    isOverRosterSize: determineOverRosterSize(roster),
                    isOverBudget: determineOverBudget(spent),
                    pointsMode: showPoints,
                    fantasyPoints: calculateFantasyPointsTotal(roster)
                }
            }}/>
            <PlayerInfoDialog open={openPlayerInfo} playerRow={playerInfoRow} handleClose={handleClosePlayerInfo}
                              handleOpenDropDialog={handleOpenDropDialog} checkDroppable={playerIsDroppable}/>
            <DropPlayerDialog open={dropDialogOpen} handleClose={handleCloseDropDialog} dropRow={playerInfoRow}
                              handleDropPlayer={handleDropPlayer}/>
            <PlayerStatsDialog open={openPlayerStats} playerRow={playerStatsRow} seasonYear={seasonYear} weekNum={weekNum}
                               handleClose={handleClosePlayerStats}/>
        </div>
    );
}

export const RosterGridFooter = ({spent, spentSalaries, spentPenalties, isOverRosterSize, isOverBudget, pointsMode, fantasyPoints}) => {
    return (
        <div style={{backgroundColor: "white"}}>
            <div style={{padding: 20, float: 'left', textAlign: 'left'}}>
                {isOverRosterSize && <Typography variant="h5" color="error">TOO MANY PLAYERS</Typography>}
                {isOverBudget && <Typography variant="h5" color="error">OVER BUDGET</Typography>}
            </div>
            {spent &&
                <div style={{padding: 20, float: 'right', textAlign: 'right'}}>
                    {!pointsMode ?
                        <Box sx={{display: 'flex', gap: 6}}>
                            <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 150}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <Typography variant="h6" color="inherit">Salaries:</Typography>
                                    <Typography variant="h6" color="inherit">{spentSalaries}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <Typography variant="h6" color="inherit">Penalties:</Typography>
                                    <Typography variant="h6" color="inherit">{spentPenalties}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 150}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <Typography variant="h6" color="inherit">Total Spent:</Typography>
                                    <Typography variant="h6" color="inherit">{spent}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <Typography variant="h6" color="inherit">Available:</Typography>
                                    <Typography variant="h6" color="inherit"><b>{config.salaryCap - spent}</b></Typography>
                                </Box>
                            </Box>
                        </Box>
                        :
                        <div>
                            <Typography variant="h6" color="inherit">Total: {fantasyPoints.toFixed(2)}</Typography>
                            <div style={{minHeight: 32}}/>
                        </div>
                    }
                </div>
            }
        </div>
    );
}